import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { VideoDashboardStore, Video, VideoStatus } from "./types";
import { DEFAULT_FOLDERS } from "./initialState";
import { produce } from "immer";

// Create action creators
import { createFolderActions } from "./actions";
import { createViewActions } from "./actions";
import { createVideoActions } from "./actions";

// Initial state for the store
const initialState: Omit<VideoDashboardStore,
  | keyof ReturnType<typeof createFolderActions>
  | keyof ReturnType<typeof createViewActions>
  | keyof ReturnType<typeof createVideoActions>
  | 'removeVideo'
  | 'updateVideo'
  | 'setDensityMode'> = {
  videos: [], // Inicializa vazio - os vídeos virão da API
  folders: DEFAULT_FOLDERS,
  activeFolder: null,
  viewMode: 'grid',
  searchQuery: '',
  statusFilter: 'all',
  densityMode: 'comfortable'
};

// Create Zustand store
const createVideoDashboardStore = () =>
  create<VideoDashboardStore>()(
    devtools((set, get) => {
      
      // Create action creators
      const folderActions = createFolderActions(set, get);
      const viewActions = createViewActions(set);
      const videoActions = createVideoActions(set);

      return {
        ...initialState,
        ...folderActions,
        ...viewActions,
        ...videoActions,

        // Additional actions
        setStatusFilter: (status) => set(
          produce((state) => {
            state.statusFilter = status;
          })
        ),

        setSearchQuery: (query) => set(
          produce((state) => {
            state.searchQuery = query;
          })
        ),

        setSortOption: (option) => set(
          produce((state) => {
            state.sortOption = option;
          })
        ),

        setSelectedFolder: (folderId) => set(
          produce((state) => {
            state.selectedFolder = folderId;
          })
        ),

        addVideo: (video) => set(
          produce((state) => {
            state.videos.push(video);
          })
        ),

        removeVideo: (videoId) => set(
          produce((state) => {
            state.videos = state.videos.filter((v) => v.id !== videoId);
          })
        ),

        updateVideo: (videoId, data) => set(
          produce((state) => {
            const videoIndex = state.videos.findIndex(v => v.id === videoId);
            if (videoIndex !== -1) {
              state.videos[videoIndex] = { ...state.videos[videoIndex], ...data };
            }
          })
        ),

        // Keep existing code for getFolderById, getVideoById, getFilteredVideos, etc.
        getFolderById: (folderId) => {
          return get().folders.find((folder) => folder.id === folderId);
        },

        getVideoById: (videoId) => {
          return get().videos.find((video) => video.id === videoId);
        },

        getFilteredVideos: () => {
          const state = get();
          let filteredVideos = [...state.videos];
  
          // Filter by folder
          if (state.activeFolder) {
            filteredVideos = filteredVideos.filter(video => video.folderId === state.activeFolder);
          }
        
          // Filter by search query
          if (state.searchQuery) {
            const query = state.searchQuery.toLowerCase();
            filteredVideos = filteredVideos.filter(video => 
              video.title.toLowerCase().includes(query)
            );
          }
        
          // Filter by status
          if (state.statusFilter === 'processing') {
            filteredVideos = filteredVideos.filter(video => video.status === 'processing');
          } else if (state.statusFilter === 'processed') {
            filteredVideos = filteredVideos.filter(video => video.status === 'processed');
          }
        
          return filteredVideos;
        },

        getVisibleFolders: () => {
          // Keep existing code
          return get().folders;
        },

        toggleView: () => set(
          produce((state) => {
            state.viewMode = state.viewMode === "grid" ? "list" : "grid";
          })
        ),

        setDensityMode: (mode: 'comfortable' | 'compact') => set(
          produce((state) => {
            state.densityMode = mode;
          })
        ),
      };
    })
  );

export const useVideoDashboardStore = createVideoDashboardStore();
